// ============================================================================
// Deal defaults, seed data, and the repository (data-access layer).
// The repository works in TWO modes automatically:
//   - LOCAL  : no Supabase env -> data persists in localStorage (dev / demo)
//   - CLOUD  : Supabase env present -> data persists in Postgres (per-tenant, RLS)
// Components never care which; they just await these functions.
// ============================================================================

import type { Deal } from "../types";
import { getSupabase, isCloud } from "./supabase";
import { westDixieDeal } from "../data/westDixie";

let counter = 0;
export function newId(): string {
  counter += 1;
  return "d" + Date.now().toString(36) + counter.toString(36);
}

/** A blank deal seeded with sensible Florida defaults. */
export function baseDeal(over: Partial<Deal> = {}): Deal {
  return {
    id: newId(),
    name: "New Property",
    addr: "",
    market: "Hillsborough (Tampa)",
    stage: "Screening",
    u2c: 4, u2r: 1950, u1c: 2, u1r: 1600,
    yearBuilt: 1985, roofAge: 10, flood: "X", oz: false, zoning: true,
    qct: "unknown", dda: "unknown", usdaRural: "unknown",
    lisPendens: false, openLiens: 0,
    safmr2: 1930, paymentStd: 1.03, ua: 110,
    vacPct: 0.05, otherInc: 1200,
    taxes: 16000, insBase: 17000, insStress: 25000, mgmtPct: 0.08,
    repairs: 7000, utilities: 4500, admin: 3400, reserves: 3000,
    purchase: 700000, rehab: 175000, soft: 55000, hasDeficiencies: false,
    bridgeLTC: 0.75, permRate: 0.0625, amort: 30, exitCap: 0.065,
    targetLTV: 0.75, dscrMin: 1.25,
    lp1: 120000, lp2: 80000, lp3: 40000, sponsorCo: 40000,
    prefPct: 0.08, gpPromote: 0.3,
    rentGrow: 0.03, expGrow: 0.035,
    popGrow: 0.11, renterRising: true, unemp: 0.036, comps: 2,
    concHigh: false, phaActive: true, samClear: true,
    ...over,
  };
}

/** Three illustrative Florida deals so a fresh workspace isn't empty. */
export function seedDeals(): Deal[] {
  return [
    westDixieDeal(baseDeal),
    baseDeal({
      id: "palms", name: "The Palms on 12th",
      addr: "1420 N 12th St, Tampa, FL 33602", market: "Hillsborough (Tampa)",
      stage: "Underwriting", yearBuilt: 1979, roofAge: 16, flood: "AE", oz: true, comps: 3,
      qct: "yes", dda: "no", usdaRural: "no",
    }),
    baseDeal({
      id: "rowan", name: "Rowan Court 8",
      addr: "337 Rowan Ct, Jacksonville, FL", market: "Duval (Jacksonville)", stage: "Screening",
      u2c: 5, u2r: 1500, u1c: 3, u1r: 1200, purchase: 640000, rehab: 150000, soft: 45000,
      safmr2: 1560, insBase: 14000, insStress: 20000, taxes: 12000, roofAge: 19, flood: "AE",
      oz: false, popGrow: 0.06, unemp: 0.041, comps: 1, concHigh: true,
      qct: "yes", dda: "yes", usdaRural: "no",
      lisPendens: true, openLiens: 18000, // distressed seller: pre-foreclosure + a code-enforcement lien
    }),
    baseDeal({
      id: "grove", name: "Grove Street Duplex",
      addr: "908 E 26th Ave, Tampa, FL", market: "Hillsborough (Tampa)", stage: "Screening",
      u2c: 1, u2r: 1900, u1c: 1, u1r: 1550, purchase: 340000, rehab: 70000, soft: 26000,
      safmr2: 1930, insBase: 9000, insStress: 13000, taxes: 7000, roofAge: 8, flood: "X",
      oz: false, comps: 2, lp1: 60000, lp2: 40000, lp3: 0,
      qct: "no", dda: "no", usdaRural: "unknown",
    }),
  ];
}

// ---------------------------------------------------------------------------
// Repository
// ---------------------------------------------------------------------------
const LS_KEY = "beacon_deals";

function upgradeWestDixie(deal: Deal): Deal {
  if (deal.id !== "west-dixie-15458") return deal;
  const canonical = westDixieDeal(baseDeal).development!;
  const current = deal.development;
  const research = current?.research && current.research.version >= canonical.research.version
    ? current.research
    : { ...canonical.research, notes: current?.research?.notes ?? [] };
  return {
    ...deal,
    kind: "land-development",
    development: {
      ...canonical,
      ...current,
      facts: canonical.facts,
      risks: canonical.risks,
      sourceLinks: canonical.sourceLinks,
      budget: { ...canonical.budget, ...(current?.budget ?? {}) },
      readiness: { ...(canonical.readiness ?? {}), ...(current?.readiness ?? {}) },
      analysis: current?.analysis,
      research,
      entitlementPath: canonical.entitlementPath,
      recommendation: canonical.recommendation,
      asOf: canonical.asOf,
    },
  };
}

function localAll(): Deal[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as Deal[];
      // Non-destructive seed migration: add the West Dixie working file once,
      // then preserve every user edit made to it in later sessions.
      if (!saved.some((d) => d.id === "west-dixie-15458")) {
        saved.push(westDixieDeal(baseDeal));
        localSaveAll(saved);
      }
      const upgraded = saved.map(upgradeWestDixie);
      if (JSON.stringify(upgraded) !== JSON.stringify(saved)) localSaveAll(upgraded);
      return upgraded;
    }
  } catch {
    /* ignore */
  }
  const seeded = seedDeals();
  localSaveAll(seeded);
  return seeded;
}
function localSaveAll(deals: Deal[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(deals));
  } catch {
    /* ignore */
  }
}

export async function listDeals(): Promise<Deal[]> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { data, error } = await sb.from("deals").select("*").order("name");
      if (error) throw error;
      if (data && data.length) return (data as Deal[]).map(upgradeWestDixie);
      // Connected, but the cloud table is empty / not set up yet — show the
      // local seed so the app is never blank while the backend is being built.
      return localAll();
    } catch {
      return localAll(); // cloud not ready (no tables / RLS) — graceful fallback
    }
  }
  return localAll();
}

export async function saveDeal(deal: Deal): Promise<void> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { error } = await sb.from("deals").upsert(deal);
      if (error) throw error;
      return;
    } catch {
      /* cloud not ready — save locally so the user never loses work */
    }
  }
  const all = localAll();
  const i = all.findIndex((d) => d.id === deal.id);
  if (i >= 0) all[i] = deal;
  else all.push(deal);
  localSaveAll(all);
}

export async function deleteDeal(id: string): Promise<void> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { error } = await sb.from("deals").delete().eq("id", id);
      if (error) throw error;
      return;
    } catch {
      /* cloud not ready — fall through to local */
    }
  }
  localSaveAll(localAll().filter((d) => d.id !== id));
}
